/**
 * HeroSRE Component
 * =================
 * Hero section with SRE Engine focus
 * ALL content from Blueprint - NO hardcoded text or routes
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint, BlueprintSection } from '../../context/BlueprintContext';
import { ArrowRight, Eye } from 'lucide-react';

const HeroSRE = () => {
  const { homepage, getRoute, loading } = useBlueprint();
  const hero = homepage?.hero || {};
  const badge = hero?.badge || {};
  
  // Safe text extraction helper
  const safeText = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.text) return safeText(value.text, fallback);
      if (value.label) return safeText(value.label, fallback);
      return fallback;
    }
    return fallback;
  };
  
  // Get SRE route from config (NO hardcoded route)
  const sreRoute = getRoute('sre', '/sre');
  
  // CTA can be string or {text, route} object
  const ctaText = safeText(hero.cta, 'Inizia');
  const ctaLink = typeof hero.cta === 'object' ? (hero.cta.route || hero.cta.link) : (hero.ctaLink || sreRoute);
  
  // Safe values
  const labelText = safeText(hero.label, '');
  const badgeTop = safeText(badge.top, '');
  const badgeBottom = safeText(badge.bottom, '');
  
  // If no hero data and not loading, show placeholder
  if (!hero.title && !hero.title_it && !hero.label && !loading) {
    return (
      <section className="min-h-[60vh] bg-[var(--cream)] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#888]">Homepage Hero non configurato</p>
          <p className="text-xs text-[#AAA] mt-2">Configura ui.home.hero nel Blueprint Admin</p>
        </div>
      </section>
    );
  }
  
  // Parse title with highlights - supports line1, line1Highlight, line2, line2Highlight
  // Also supports _it suffix for Italian translations
  const renderTitle = () => {
    // Try Italian version first, then fallback to default
    const line1 = hero.line1_it || hero.line1 || hero.title_it || hero.title || '';
    const line1Highlight = hero.line1Highlight_it || hero.line1Highlight || hero.titleHighlight || '';
    const line2 = hero.line2_it || hero.line2 || hero.subtitle_it || hero.subtitle || '';
    const line2Highlight = hero.line2Highlight_it || hero.line2Highlight || '';
    
    const renderLine = (text, highlight) => {
      if (!text) return null;
      if (!highlight) return text;
      
      // Find and highlight the word
      const highlightClean = highlight.replace(/[.!?]/g, '');
      const idx = text.toLowerCase().indexOf(highlightClean.toLowerCase());
      
      if (idx === -1) return text;
      
      const before = text.slice(0, idx);
      const word = text.slice(idx, idx + highlightClean.length);
      const after = text.slice(idx + highlightClean.length);
      
      return (
        <>
          {before}
          <span className="text-[var(--gold)]">{word}</span>
          {after}
        </>
      );
    };
    
    return (
      <>
        {line1 && <>{renderLine(line1, line1Highlight)}</>}
        {line1 && line2 && <br />}
        {line2 && <>{renderLine(line2, line2Highlight)}</>}
      </>
    );
  };
  
  // Parse description with newlines
  const renderDescription = () => {
    const desc = hero.description || '';
    return desc.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < desc.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  return (
    <BlueprintSection name="hero">
      <section className="pt-[68px] bg-[var(--cream)]">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
          {/* Left - Content */}
          <div className="flex flex-col justify-center px-6 lg:px-14 xl:px-[88px] py-12 lg:py-20 bg-[var(--cream)]">
            <p className="sr-label mb-8">
              {labelText}
            </p>
            
            <h1 className="sr-title-hero mb-6">
              {renderTitle()}
            </h1>
            
            <p className="sr-description mb-11">
              {renderDescription()}
            </p>
            
            <Link
              to={ctaLink}
              className="sr-btn-primary w-fit"
            >
              {ctaText}
              <ArrowRight size={15} className="arrow" />
            </Link>
          </div>
          
          {/* Right - Image */}
          <div className="relative overflow-hidden min-h-[300px] lg:min-h-[500px]">
            <img
              src={hero.image}
              alt="Luxury marble interior"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            
            {/* SRE Badge */}
            <div className="absolute top-5 right-5 bg-black/[0.88] backdrop-blur-[14px] border border-white/10 rounded-[10px] px-4 py-3 flex items-center gap-3">
              <div>
                <div className="text-[9px] font-bold tracking-[2.5px] text-[var(--gold)] mb-0.5">
                  {badgeTop}
                </div>
                <div className="text-[11px] text-[#CCC]">
                  {badgeBottom}
                </div>
              </div>
              <div className="w-[34px] h-[34px] rounded-full bg-[var(--gold)] flex items-center justify-center">
                <Eye size={16} className="text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </BlueprintSection>
  );
};

export default HeroSRE;
